import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Users, UserCheck, TrendingUp, Download, Search, RefreshCcw, Mail, History } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const Stat = ({ icon: Icon, label, value, accent }) => (
  <Card data-testid={`mkt-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ background: accent || '#fdf1f3', color: '#722F37' }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const ContestMarketingTab = () => {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contest/marketing/stats`, { headers: authHeaders() });
      setStats(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contest/marketing/subscribers`, {
        headers: authHeaders(),
        params: { search, status: statusFilter, limit: 200 },
      });
      setSubs(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({ title: 'Could not load subscribers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadSubs, 250);
    return () => clearTimeout(t);
  }, [loadSubs]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/contest/marketing/export.csv`, {
        headers: authHeaders(), params: { status: statusFilter }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lokal-marketing-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export started' });
    } catch (e) {
      toast({ title: 'Export failed', variant: 'destructive' });
    }
  };

  const toggleStatus = async (s) => {
    const next = s.status === 'subscribed' ? 'unsubscribed' : 'subscribed';
    try {
      await axios.patch(`${API}/contest/marketing/subscribers/${s.id}`, { status: next }, { headers: authHeaders() });
      toast({ title: next === 'subscribed' ? 'Re-subscribed' : 'Unsubscribed' });
      loadSubs(); loadStats();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const maxGrowth = Math.max(1, ...((stats?.growth || []).map((g) => g.count)));

  return (
    <div className="space-y-6" data-testid="marketing-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Marketing &amp; Subscribers</h2>
          <p className="text-sm text-gray-500">Permission-based opt-ins captured during Wine of the Year voting (CASL-compliant).</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadStats(); loadSubs(); }} data-testid="mkt-refresh">
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={UserCheck} label="Verified Voters" value={stats?.verifiedVoters ?? '—'} accent="#ecf7ec" />
        <Stat icon={Users} label="Subscribers" value={stats?.totalSubscribers ?? '—'} />
        <Stat icon={TrendingUp} label="Opt-in Rate" value={stats ? `${stats.conversionRate}%` : '—'} accent="#FFFBEA" />
        <Stat icon={Mail} label="Unsubscribed" value={stats?.totalUnsubscribed ?? '—'} accent="#f3f4f6" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={TrendingUp} label="New Today" value={stats?.newToday ?? '—'} />
        <Stat icon={TrendingUp} label="This Week" value={stats?.newThisWeek ?? '—'} />
        <Stat icon={TrendingUp} label="This Month" value={stats?.newThisMonth ?? '—'} />
      </div>

      {/* Growth sparkline */}
      {stats?.growth?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Subscriber growth (last 12 weeks)</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-24" data-testid="mkt-growth-chart">
              {stats.growth.map((g, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${g.weekEnding}: ${g.count}`}>
                  <div className="w-full rounded-t" style={{ height: `${(g.count / maxGrowth) * 100}%`, minHeight: g.count ? 4 : 0, background: '#722F37' }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>{stats.growth[0]?.weekEnding}</span>
              <span>{stats.growth[stats.growth.length - 1]?.weekEnding}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Directory controls */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input className="pl-9" placeholder="Search by email or name…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="mkt-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-gray-300 text-sm bg-white" data-testid="mkt-status-filter">
          <option value="">All statuses</option>
          <option value="subscribed">Subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <Button variant="outline" onClick={exportCsv} data-testid="mkt-export">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Directory */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left">
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Opted in</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Voted for</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="mkt-subscriber-rows">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400" data-testid="mkt-empty">No subscribers yet. They'll appear here as voters opt in.</td></tr>
              ) : subs.map((s) => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{s.email}</td>
                  <td className="px-4 py-2.5 text-gray-600">{s.firstName || '—'}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant={s.status === 'subscribed' ? 'default' : 'secondary'}
                      style={s.status === 'subscribed' ? { background: '#722F37' } : {}}>
                      {s.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{fmtDate(s.consentAt)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{s.votedArtistName || '—'}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(s)} data-testid={`mkt-history-${s.id}`}>
                      <History className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(s)} data-testid={`mkt-toggle-${s.id}`}>
                      {s.status === 'subscribed' ? 'Unsubscribe' : 'Re-subscribe'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-gray-400">{total} record{total === 1 ? '' : 's'} total. Consent wording &amp; version are stored with each opt-in for CASL compliance.</p>

      {/* Consent history dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="mkt-history-dialog">
          <DialogHeader>
            <DialogTitle>Consent history</DialogTitle>
            <DialogDescription>{detail?.email}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                <div><strong>Source:</strong> {detail.source}</div>
                <div><strong>Consent version:</strong> {detail.consentVersion || '—'}</div>
                {detail.consentWording && <div className="mt-1 italic text-gray-500">"{detail.consentWording}"</div>}
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {(detail.consentHistory || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                    <span className={e.action === 'subscribe' || e.action === 'subscribed' ? 'text-green-700' : 'text-gray-600'}>
                      {e.action}
                    </span>
                    <span className="text-xs text-gray-400">{e.source} · {fmtDate(e.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContestMarketingTab;
